import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { useNotifications } from 'providers/notifications/useNotifications';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetAccountQuery, useUpdateAccountMutation } from 'services/accounts';
import { useListEventDestinationsQuery, useSyncWebhookDestinationMutation } from 'services/eventDestinations';
import { webhookFieldFromDestinations } from 'services/eventDestinations/planWebhookSync';

import selectAccount from 'state/selectors/accounts/accountSelector';
import { selectUserLevel } from 'state/slices/userSlice';

import { AccountSettings } from 'types/account';
import { APIError, ValidationErrorMeta } from 'types/error';
import { NotificationSeverity } from 'types/notifications';
import { UserAccessLevel } from 'types/user';

import { getValidationDetails } from 'utils/error';

import { getFormValues } from '../../utils';
import { GeneralSection } from './general';
import { formFields } from './keys';

export const GeneralSectionContainer = () => {
  const { pushToast } = useNotifications();
  const { account } = useSelector(selectAccount);
  const [accountData, setAccountData] = useState<AccountSettings>(getFormValues(account, formFields) as AccountSettings);
  const [values, setValues] = useState<AccountSettings>(accountData);
  const [saveAccountInfo, { isLoading: isUpdating, error, reset }] = useUpdateAccountMutation();
  const [syncWebhook, { isLoading: isSyncingWebhook, error: webhookError, reset: resetWebhook }] = useSyncWebhookDestinationMutation();
  const userLevel = useSelector(selectUserLevel);
  const { data } = useGetAccountQuery(account.uuid!, {
    refetchOnMountOrArgChange: true,
    skip: !account.uuid,
  });
  const { data: destinations, isSuccess: destinationsLoaded } = useListEventDestinationsQuery(account.uuid ?? '', {
    skip: !account.uuid,
  });
  const webhookSeededFor = useRef<string | null>(null);
  const valuesRef = useRef(values);
  const accountDataRef = useRef(accountData);
  valuesRef.current = values;
  accountDataRef.current = accountData;

  useEffect(() => {
    if (!data || !isEqual(valuesRef.current, accountDataRef.current)) {
      return;
    }

    const newValues = getFormValues(account, formFields) as AccountSettings;
    const keepWebhook = webhookSeededFor.current === account.uuid;
    const nextValues = {
      ...newValues,
      webhookUrl: keepWebhook ? valuesRef.current.webhookUrl : newValues.webhookUrl,
    };

    setValues(nextValues);
    setAccountData(nextValues);
  }, [account.uuid, data]);

  useEffect(() => {
    if (!destinationsLoaded || !account.uuid || webhookSeededFor.current === account.uuid) {
      return;
    }

    webhookSeededFor.current = account.uuid;
    const destinationWebhook = webhookFieldFromDestinations(destinations?.data ?? []);
    const webhookUntouched = valuesRef.current.webhookUrl === accountDataRef.current.webhookUrl;
    if (destinationWebhook === undefined || !webhookUntouched) {
      return;
    }

    setValues((prev) => ({ ...prev, webhookUrl: destinationWebhook }));
    setAccountData((prev) => ({ ...prev, webhookUrl: destinationWebhook }));
  }, [account.uuid, destinations, destinationsLoaded]);

  useEffect(() => {
    const saveError = error || webhookError;
    if (saveError && 'data' in saveError) {
      const { errorMessage } = saveError.data as APIError;
      pushToast({
        severity: NotificationSeverity.error,
        message: errorMessage,
      });
    }
  }, [error, webhookError]);

  const handleFormValueChange = (key: string, value: any) => {
    setValues({
      ...values,
      [key]: value,
    });
    reset();
    resetWebhook();
  };

  const isAdmin = Boolean(userLevel && userLevel >= UserAccessLevel.Admin);
  const isS4cUser = Boolean(userLevel && userLevel >= UserAccessLevel.Support);
  const isPartner = Boolean(userLevel && userLevel === UserAccessLevel.Partner);
  const disabledFields = {
    isCertified: !isS4cUser,
    processingFee: !isS4cUser,
    processingFeeEEA: !isS4cUser,
    onRampEnabled: !isS4cUser,
    offRampEnabled: !isS4cUser,
    areCustomerEmailsEnabled: isPartner,
  };

  const readOnlyMode = Boolean(userLevel && userLevel === UserAccessLevel.Support);

  const resetFormValues = () => {
    setValues(accountData);
    reset();
    resetWebhook();
  };

  const saveFormValues = async () => {
    if (readOnlyMode) {
      return;
    }

    const disabledFieldKeys = Object.entries(disabledFields)
      .filter(([, value]) => Boolean(value))
      .map(([key]) => key);

    const diffValues = (Object.keys(values) as (keyof AccountSettings)[])
      .filter((key) => account[key] !== values[key])
      .reduce((result, key) => ({ ...result, [key]: values[key] }), { uuid: account.uuid });

    const preparedValues = omit(diffValues, [...disabledFieldKeys, 'webhookUrl']);
    const hasAccountChanges = Object.keys(preparedValues).some((key) => key !== 'uuid');
    if (hasAccountChanges) {
      const saved = await saveAccountInfo(preparedValues);
      if ('error' in saved) {
        return;
      }
    }

    if (values.webhookUrl !== accountData.webhookUrl) {
      const synced = await syncWebhook({
        accountUuid: account.uuid,
        previousField: accountData.webhookUrl,
        nextField: values.webhookUrl,
      });
      if ('error' in synced) {
        return;
      }
    }

    setAccountData((prev) => ({ ...prev, ...diffValues, webhookUrl: values.webhookUrl }));
  };

  const isFormChanged = !isEqual(values, accountData);
  const saveErrorMeta = error ? ((error as FetchBaseQueryError).data as { meta: ValidationErrorMeta }).meta : undefined;

  const saveError = saveErrorMeta ? getValidationDetails(saveErrorMeta) : undefined;
  const isSaveAvailable = isFormChanged && isEmpty(saveError);

  return (
    <GeneralSection
      account={values}
      onFormValueChange={handleFormValueChange}
      isFormChanged={isFormChanged}
      isSaveAvailable={isSaveAvailable}
      resetFormValues={resetFormValues}
      saveFormValues={saveFormValues}
      disabledFields={disabledFields}
      error={saveError}
      isUpdating={isUpdating || isSyncingWebhook}
      readOnlyMode={readOnlyMode}
      isAdmin={isAdmin}
    />
  );
};
